import React, { useMemo } from 'react';
import DotWaveLoader from '../commonComponents/DotWaveLoader';
import PropTypes from 'prop-types';
import { Box, Paper, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { cardClasses } from '../../styles/tailwindClasses';
import { posSizingTableTitleSx } from '../pos_sizing_table/tableStyles';
import {
  ACTUAL_BAR_COLOR,
  OPTIMIZED_BAR_COLOR,
  riskOverviewPanelSx,
} from '../../utils/riskOverviewStyles';

const asNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const clampPercent = (value) => Math.max(0, Math.min(100, value));

const formatInteger = (value) => (
  value == null ? '—' : Math.round(value).toLocaleString()
);

const formatPercent = (value) => (
  value == null ? '—' : `${value.toFixed(2)}%`
);

const formatMoney = (value) => (
  value == null ? '—' : `${value < 0 ? '-' : ''}$${formatInteger(Math.abs(value))}`
);

const percentOf = (value, denominator) => (
  value == null || denominator == null || denominator <= 0
    ? null
    : (value / denominator) * 100
);

const formatCompact = (value) => (
  value == null
    ? '—'
    : new Intl.NumberFormat(undefined, {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value)
);

const buildPortfolioProfile = (calculations = {}) => {
  const rawFactorRisk = asNumber(calculations.factorRiskPct);
  const rawIdioRisk = asNumber(calculations.idioRiskPct);
  const factorRiskPct = rawFactorRisk == null && rawIdioRisk != null
    ? 100 - rawIdioRisk
    : rawFactorRisk;
  const idioRiskPct = rawIdioRisk == null && rawFactorRisk != null
    ? 100 - rawFactorRisk
    : rawIdioRisk;
  const gmv = asNumber(calculations.gmv);
  const nmv = asNumber(calculations.nmv);
  const hasGmvProfile = gmv != null && nmv != null;

  return Object.freeze({
    factorRiskPct,
    idioRiskPct,
    totalRiskDollar: asNumber(calculations.totalRiskDollar),
    gmv,
    shortGmv: hasGmvProfile ? -Math.max(0, (gmv - nmv) / 2) : null,
    longGmv: hasGmvProfile ? Math.max(0, (gmv + nmv) / 2) : null,
    netExposure: nmv,
    betaExposure: asNumber(calculations.betaExp),
  });
};

export function buildRiskOverviewInsights(calculations = {}, optimizedCalculations = {}) {
  const actual = buildPortfolioProfile(calculations);
  const optimized = buildPortfolioProfile(optimizedCalculations);

  return Object.freeze({
    ...actual,
    actual,
    optimized,
  });
}

function EmptyState({ children }) {
  return (
    <Box
      sx={{
        flex: 1,
        display: 'grid',
        placeItems: 'center',
        minHeight: 90,
        color: 'text.secondary',
        textAlign: 'center',
        px: 2,
      }}
    >
      <Typography variant="body2">{children}</Typography>
    </Box>
  );
}

function RiskDonut({ label, profile, optimized = false, compact = false }) {
  const factorRiskPct = clampPercent(profile.factorRiskPct ?? 0);
  const factorColor = optimized ? OPTIMIZED_BAR_COLOR : 'primary.main';
  const remainderColor = optimized
    ? 'rgb(var(--c-optimized) / 0.24)'
    : (theme) => alpha(theme.palette.info.main, 0.22);

  return (
    <Box
      sx={{
        minWidth: 0,
        display: 'grid',
        gridTemplateRows: 'auto auto auto',
        justifyItems: 'center',
        alignContent: 'space-between',
        gap: 0.8,
        width: '100%',
        height: '100%',
      }}
    >
      <Typography
        variant="caption"
        sx={{ fontWeight: 800, color: optimized ? OPTIMIZED_BAR_COLOR : 'text.primary' }}
      >
        {label}
      </Typography>
      <Box
        role="img"
        aria-label={`${label}: factor risk ${formatPercent(profile.factorRiskPct)}; idiosyncratic risk ${formatPercent(profile.idioRiskPct)}`}
        sx={{
          width: compact ? 'min(118px, 100%)' : 'min(142px, 100%)',
          aspectRatio: '1',
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          background: (theme) => `conic-gradient(${optimized ? OPTIMIZED_BAR_COLOR : theme.palette.primary.main} 0 ${factorRiskPct}%, ${typeof remainderColor === 'function' ? remainderColor(theme) : remainderColor} ${factorRiskPct}% 100%)`,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: '18%',
            borderRadius: '50%',
            backgroundColor: 'background.paper',
          },
        }}
      >
        <Box sx={{ zIndex: 1, textAlign: 'center', px: 0.5 }}>
          <Typography
            sx={{
              fontSize: compact ? '0.9rem' : '1.05rem',
              fontWeight: 800,
              lineHeight: 1.1,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formatInteger(profile.totalRiskDollar)}
          </Typography>
          <Typography sx={{ fontSize: compact ? '0.66rem' : '0.74rem' }} color="text.secondary">
            Total Risk ($)
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'grid', gap: 0.4, width: '100%', maxWidth: compact ? 176 : 190 }}>
        {[
          ['Factor Risk', profile.factorRiskPct, factorColor],
          ['Idio Risk', profile.idioRiskPct, remainderColor],
        ].map(([riskLabel, value, color]) => (
          <Box
            key={riskLabel}
            sx={{
              display: 'grid',
              gridTemplateColumns: '8px minmax(60px, 1fr) auto',
              alignItems: 'center',
              columnGap: 0.55,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: color,
              }}
            />
            <Typography noWrap sx={{ fontSize: '0.69rem' }} color="text.secondary">
              {riskLabel}
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
              {formatPercent(value)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function ExposureMarker({ value, extent, color, shape, label }) {
  if (value == null) return null;
  const left = 50 + ((value / extent) * 48);
  const markerSx = shape === 'diamond'
    ? {
      width: 8,
      height: 8,
      bgcolor: 'background.paper',
      border: `2px solid ${color}`,
      transform: 'translate(-50%, -50%) rotate(45deg)',
    }
    : {
      width: 9,
      height: 9,
      bgcolor: color,
      borderRadius: '50%',
      border: '1px solid rgba(255,255,255,0.9)',
      transform: 'translate(-50%, -50%)',
    };

  return (
    <Box
      component="span"
      aria-label={`${label} ${formatInteger(value)}`}
      sx={{
        position: 'absolute',
        left: `${Math.max(2, Math.min(98, left))}%`,
        top: '50%',
        zIndex: 3,
        ...markerSx,
      }}
    />
  );
}

function ExposureProfileTooltip({ label, profile, aum }) {
  const metrics = [
    ['Gross GMV', profile.gmv],
    ['Long GMV', profile.longGmv],
    ['Short GMV', profile.shortGmv],
    ['Net GMV', profile.netExposure],
    ['Beta exposure', profile.betaExposure],
  ];
  return (
    <Box sx={{ minWidth: aum > 0 ? 320 : 275, p: 0.5 }}>
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, mb: 0.25 }}>{label} exposure</Typography>
      <Typography sx={{ fontSize: '0.67rem', opacity: 0.75, mb: 1 }}>
        Dollar exposures and shares of gross market value{aum > 0 ? ' and account AUM' : ''}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: aum > 0 ? '1fr auto auto auto' : '1fr auto auto', gap: 0.8, mb: 0.35, pb: 0.5, borderBottom: '1px solid rgba(255,255,255,0.25)' }}>
        <Typography sx={{ fontSize: '0.65rem', opacity: 0.7 }}>Metric</Typography>
        <Typography sx={{ fontSize: '0.65rem', opacity: 0.7, textAlign: 'right' }}>USD</Typography>
        <Typography sx={{ fontSize: '0.65rem', opacity: 0.7, textAlign: 'right' }}>% GMV</Typography>
        {aum > 0 && <Typography sx={{ fontSize: '0.65rem', opacity: 0.7, textAlign: 'right' }}>% AUM</Typography>}
      </Box>
      {metrics.map(([name, value]) => (
        <Box key={name} sx={{ display: 'grid', gridTemplateColumns: aum > 0 ? '1fr auto auto auto' : '1fr auto auto', gap: 0.8, py: 0.4, fontVariantNumeric: 'tabular-nums' }}>
          <Typography sx={{ fontSize: '0.71rem', fontWeight: name === 'Net GMV' || name === 'Beta exposure' ? 800 : 500 }}>{name}</Typography>
          <Typography sx={{ fontSize: '0.71rem', textAlign: 'right' }}>{formatMoney(value)}</Typography>
          <Typography sx={{ fontSize: '0.71rem', textAlign: 'right' }}>{formatPercent(percentOf(value, profile.gmv))}</Typography>
          {aum > 0 && <Typography sx={{ fontSize: '0.71rem', textAlign: 'right' }}>{formatPercent(percentOf(value, aum))}</Typography>}
        </Box>
      ))}
    </Box>
  );
}

function ExposureProfileRow({ label, profile, extent, optimized = false, aum = null }) {
  const color = optimized ? OPTIMIZED_BAR_COLOR : ACTUAL_BAR_COLOR;
  const shortWidth = profile.shortGmv == null
    ? 0
    : (Math.abs(profile.shortGmv) / extent) * 48;
  const longWidth = profile.longGmv == null
    ? 0
    : (profile.longGmv / extent) * 48;

  return (
    <Box
      aria-label={`${label} exposure profile`}
      sx={{
        display: 'grid',
        gridTemplateColumns: '72px minmax(0, 1fr)',
        gap: 1,
        alignItems: 'center',
        minHeight: 0,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontSize: '0.72rem',
          fontWeight: 800,
          color: optimized ? OPTIMIZED_BAR_COLOR : 'text.primary',
        }}
      >
        {label}
      </Typography>
      <Tooltip
        arrow
        title={<ExposureProfileTooltip label={label} profile={profile} aum={aum} />}
        slotProps={{ tooltip: { sx: { maxWidth: 'none', borderRadius: 1.5, boxShadow: 6 } } }}
      >
        <Box sx={{ minWidth: 0 }} tabIndex={0} aria-label={`${label} exposure details`}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              mb: 0.25,
              color: optimized ? OPTIMIZED_BAR_COLOR : 'text.secondary',
            }}
          >
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700 }}>
              {formatCompact(profile.shortGmv)}
            </Typography>
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, textAlign: 'right' }}>
              {formatCompact(profile.longGmv)}
            </Typography>
          </Box>
          <Box
            sx={{
              height: 26,
              position: 'relative',
              bgcolor: 'action.hover',
              borderRadius: 0.75,
              overflow: 'visible',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                right: '50%',
                width: `${shortWidth}%`,
                bgcolor: color,
                opacity: optimized ? 0.34 : 0.28,
                borderRadius: '3px 0 0 3px',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: '50%',
                width: `${longWidth}%`,
                bgcolor: color,
                opacity: optimized ? 0.82 : 0.72,
                borderRadius: '0 3px 3px 0',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: -3,
                bottom: -3,
                left: '50%',
                width: '1px',
                bgcolor: 'text.secondary',
                opacity: 0.65,
                zIndex: 2,
              }}
            />
            <ExposureMarker
              value={profile.netExposure}
              extent={extent}
              color={color}
              shape="circle"
              label={`${label} net exposure`}
            />
            <ExposureMarker
              value={profile.betaExposure}
              extent={extent}
              color={color}
              shape="diamond"
              label={`${label} beta exposure`}
            />
          </Box>
        </Box>
      </Tooltip>
    </Box>
  );
}

function ExposureLegendItem({ color, shape, children }) {
  const markerSx = shape === 'diamond'
    ? {
      width: 7,
      height: 7,
      border: `2px solid ${color}`,
      transform: 'rotate(45deg)',
      bgcolor: 'background.paper',
    }
    : shape === 'circle'
      ? { width: 8, height: 8, borderRadius: '50%', bgcolor: color }
      : { width: 11, height: 7, bgcolor: color, opacity: shape === 'short' ? 0.3 : 0.78 };

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.45 }}>
      <Box sx={markerSx} />
      <Typography sx={{ fontSize: '0.67rem' }} color="text.secondary">{children}</Typography>
    </Box>
  );
}

export default function RiskOverviewInsights({
  calculations = {},
  optimizedCalculations = {},
  showOptimized = false,
  isLoading = false,
  emptyPortfolio = false,
  aum = null,
}) {
  const model = useMemo(
    () => buildRiskOverviewInsights(calculations, optimizedCalculations),
    [calculations, optimizedCalculations],
  );
  const hasActualRiskBreakdown = model.actual.factorRiskPct != null
    || model.actual.idioRiskPct != null;
  const profiles = showOptimized
    ? [model.actual, model.optimized]
    : [model.actual];
  const exposureExtent = Math.max(
    1,
    ...profiles.flatMap((profile) => [
      profile.shortGmv,
      profile.longGmv,
      profile.netExposure,
      profile.betaExposure,
    ]).filter((value) => value != null).map(Math.abs),
  );
  const hasExposureProfile = profiles.some((profile) => (
    profile.shortGmv != null
    || profile.longGmv != null
    || profile.netExposure != null
    || profile.betaExposure != null
  ));

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr)',
          lg: 'repeat(2, minmax(0, 1fr))',
          xl: 'minmax(0, 1fr)',
        },
        gridTemplateRows: {
          xs: 'minmax(236px, 1.2fr) minmax(0, 0.8fr)',
          lg: 'minmax(0, 1fr)',
          xl: 'minmax(236px, 1.2fr) minmax(0, 0.8fr)',
        },
        gap: 2,
        minWidth: 0,
        height: '100%',
      }}
    >
      <Paper
        className={cardClasses}
        elevation={0}
        aria-label="Factor Risk Breakdown"
        sx={{ ...riskOverviewPanelSx, p: 1.35, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        {isLoading ? (
          <DotWaveLoader height="100%" label="Loading factor risk breakdown" sx={{ flex: 1 }} />
        ) : emptyPortfolio || !hasActualRiskBreakdown ? (
          <EmptyState>Load a portfolio to view the risk breakdown.</EmptyState>
        ) : (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'grid',
              gridTemplateColumns: showOptimized ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)',
              alignItems: 'center',
              gap: showOptimized ? 1.2 : 2,
            }}
          >
            <RiskDonut
              label="Actual"
              profile={model.actual}
              compact={showOptimized}
            />
            {showOptimized && (
              <Box sx={{ borderLeft: 1, borderColor: 'divider', pl: 1.2, minWidth: 0, height: '100%' }}>
                <RiskDonut
                  label="Optimized"
                  profile={model.optimized}
                  optimized
                  compact
                />
              </Box>
            )}
          </Box>
        )}
      </Paper>

      <Paper
        className={cardClasses}
        elevation={0}
        sx={{ ...riskOverviewPanelSx, p: 1.35, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        <Typography variant="subtitle1" sx={posSizingTableTitleSx}>
          Exposure Profile Comparison
        </Typography>
        {isLoading ? (
          <DotWaveLoader height="100%" label="Loading exposure profile comparison" sx={{ mt: 1, flex: 1 }} />
        ) : emptyPortfolio || !hasExposureProfile ? (
          <EmptyState>Exposure metrics will appear when portfolio data is ready.</EmptyState>
        ) : (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'grid',
              gridTemplateRows: showOptimized
                ? 'repeat(2, minmax(48px, 1fr)) auto'
                : 'minmax(64px, 1fr) auto',
              alignContent: 'stretch',
              gap: 0.65,
              mt: 0.35,
            }}
          >
            <ExposureProfileRow
              label="Actual"
              profile={model.actual}
              extent={exposureExtent}
              aum={aum}
            />
            {showOptimized && (
              <ExposureProfileRow
                label="Optimized"
                profile={model.optimized}
                extent={exposureExtent}
                aum={aum}
                optimized
              />
            )}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexWrap: 'wrap',
                gap: 1.2,
                pt: 0.35,
              }}
            >
              <ExposureLegendItem color={ACTUAL_BAR_COLOR} shape="short">Short GMV</ExposureLegendItem>
              <ExposureLegendItem color={ACTUAL_BAR_COLOR} shape="long">Long GMV</ExposureLegendItem>
              <ExposureLegendItem color={ACTUAL_BAR_COLOR} shape="circle">Net Exposure</ExposureLegendItem>
              <ExposureLegendItem color={ACTUAL_BAR_COLOR} shape="diamond">Beta Exposure</ExposureLegendItem>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

EmptyState.propTypes = {
  children: PropTypes.node.isRequired,
};

const profileShape = PropTypes.shape({
  factorRiskPct: PropTypes.number,
  idioRiskPct: PropTypes.number,
  totalRiskDollar: PropTypes.number,
  gmv: PropTypes.number,
  nmv: PropTypes.number,
  betaExp: PropTypes.number,
});

RiskDonut.propTypes = {
  label: PropTypes.string.isRequired,
  profile: PropTypes.shape({
    factorRiskPct: PropTypes.number,
    idioRiskPct: PropTypes.number,
    totalRiskDollar: PropTypes.number,
  }).isRequired,
  optimized: PropTypes.bool,
  compact: PropTypes.bool,
};

ExposureMarker.propTypes = {
  value: PropTypes.number,
  extent: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired,
  shape: PropTypes.oneOf(['circle', 'diamond']).isRequired,
  label: PropTypes.string.isRequired,
};

ExposureProfileRow.propTypes = {
  label: PropTypes.string.isRequired,
  profile: PropTypes.shape({
    shortGmv: PropTypes.number,
    longGmv: PropTypes.number,
    netExposure: PropTypes.number,
    betaExposure: PropTypes.number,
  }).isRequired,
  extent: PropTypes.number.isRequired,
  optimized: PropTypes.bool,
};

ExposureLegendItem.propTypes = {
  color: PropTypes.string.isRequired,
  shape: PropTypes.oneOf(['short', 'long', 'circle', 'diamond']).isRequired,
  children: PropTypes.node.isRequired,
};

RiskOverviewInsights.propTypes = {
  calculations: profileShape,
  optimizedCalculations: profileShape,
  showOptimized: PropTypes.bool,
  isLoading: PropTypes.bool,
  emptyPortfolio: PropTypes.bool,
  aum: PropTypes.number,
};
